import {
	Bar,
	BarChart,
	CartesianGrid,
	Legend,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

interface TimeSeriesPoint {
	timestamp: string;
	tokensSaved: number;
	tokensUsed: number;
}

interface TokenSavingsChartProps {
	data: TimeSeriesPoint[];
	isLoading?: boolean;
}

export function TokenSavingsChart({ data, isLoading }: TokenSavingsChartProps) {
	if (isLoading) {
		return (
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">Token Savings</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="h-64 flex items-center justify-center">
						<div className="text-sm text-muted-foreground">
							Loading chart data...
						</div>
					</div>
				</CardContent>
			</Card>
		);
	}

	if (!data || data.length === 0) {
		return (
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">Token Savings</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="h-64 flex items-center justify-center">
						<div className="text-sm text-muted-foreground">
							No data available
						</div>
					</div>
				</CardContent>
			</Card>
		);
	}

	const formattedData = data.map((point) => ({
		...point,
		saved: point.tokensSaved,
		used: point.tokensUsed,
		label: point.timestamp.includes("T")
			? `${new Date(point.timestamp).toLocaleDateString("en-US", { month: "short", day: "numeric" })} ${new Date(point.timestamp).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}`
			: new Date(point.timestamp).toLocaleDateString("en-US", {
					month: "short",
					day: "numeric",
				}),
	}));

	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-lg">Token Savings</CardTitle>
			</CardHeader>
			<CardContent>
				<ResponsiveContainer width="100%" height={300}>
					<BarChart
						data={formattedData}
						margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
					>
						<CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
						<XAxis
							dataKey="label"
							className="text-xs"
							tick={{ fill: "currentColor" }}
							angle={-45}
							textAnchor="end"
							height={60}
						/>
						<YAxis className="text-xs" tick={{ fill: "currentColor" }} />
						<Tooltip
							contentStyle={{
								backgroundColor: "hsl(var(--card))",
								border: "1px solid hsl(var(--border))",
								borderRadius: "6px",
							}}
							labelStyle={{ color: "hsl(var(--foreground))" }}
							formatter={(value: any) =>
								value !== undefined ? Number(value).toLocaleString() : "0"
							}
						/>
						<Legend />
						<Bar
							dataKey="saved"
							fill="hsl(142, 76%, 36%)"
							name="Tokens Saved"
						/>
						<Bar dataKey="used" fill="hsl(var(--primary))" name="Tokens Used" />
					</BarChart>
				</ResponsiveContainer>
			</CardContent>
		</Card>
	);
}
